import React, { useEffect, useState } from 'react'
import { RegisterInput } from 'Components/Inputs/RegisterInput'
import { RegisterSelect } from 'Components/Inputs/RegisterSelect'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { TUpdateProfileFirstStepForm, TRegisterSecondStepForm } from 'Types/FormsTypes'
import { Button, FormControl, FormLabel, Input } from '@chakra-ui/react'
import { useRecoilState } from 'recoil'
import { otherSpecializationsAtom, specializationsAtom, userDataAtom } from 'Recoil/atoms'
import { useApiRequests } from 'API/ApiRequests'
import { useQuery } from '@tanstack/react-query'
import { operativitàOptions, ordineOptions, ospedaleOptions, titleOptions } from 'Configs/Constants'

export const FirstStep = ({
   profileData,
   nextStep,
   storeDateInState,
   stateData
}: {
   profileData?: any
   stateData?: any
   nextStep: () => void
   storeDateInState: (data: any) => void
}) => {
   const [userData] = useRecoilState(userDataAtom)
   const [specializations, setspecializations] = useRecoilState(specializationsAtom)
   const [otherSpecializations, setotherSpecializations] = useRecoilState(otherSpecializationsAtom)
   const [subSpecializations, setsubSpecializations] = useState([])
   const { getProvinces, getProfession, getProvinceRegion } = useApiRequests()

   const {
      handleSubmit,
      register,
      setValue,
      control,
      formState: { errors }
   } = useForm<TUpdateProfileFirstStepForm>({
      defaultValues: stateData || profileData
   })

   const watchProfessions = useWatch({ control, name: 'profession' })
   const watchSpecialization = useWatch({ control, name: 'specialization' })
   const watchSpecialization2 = useWatch({ control, name: 'specialization_2' })
   const watchProvience = useWatch({ control, name: 'province_enployment' })

   const provincesQuery = useQuery({
      queryKey: ['provinces'],
      queryFn: getProvinces,
      select: (data) => {
         return Object.keys(data.data.data).map((prov: any) => {
            return { id: prov, name: data.data.data[prov] }
         })
      }
   })

   const professionQuery = useQuery({
      queryKey: ['profession'],
      queryFn: getProfession
   })

   const onSubmit: SubmitHandler<TUpdateProfileFirstStepForm> = (
      data: TUpdateProfileFirstStepForm
   ) => {
      storeDateInState(data)
      nextStep()
   }

   useEffect(() => {
      if (watchProfessions) {
         const selectedProfession = professionQuery.data?.data.data.find(
            (profess: any) => profess.name === watchProfessions
         )
         setspecializations(selectedProfession?.specializations || [])
         setotherSpecializations(selectedProfession?.specializations || [])
      }
   }, [watchProfessions, professionQuery.data?.data.data])

   useEffect(() => {
      professionQuery.data?.data.data && setValue('profession', profileData.profession)
   }, [professionQuery.data?.data.data])

   useEffect(() => {
      if (watchSpecialization) {
         const selectedSpecialization = specializations.find((spec: any) => {
            return spec.name === watchSpecialization
         })
         setValue('sub_specialization', '')
         setsubSpecializations((selectedSpecialization as any)?.sub_specializations || [])
         setotherSpecializations(
            specializations.filter((spec: any) => spec.name !== watchSpecialization)
         )
      }
   }, [watchSpecialization])

   useEffect(() => {
      if (watchProvience) {
         getProvinceRegion(watchProvience).then((res) => {
            setValue('regione_enployment', res.data.data)
         })
      }
   }, [watchProvience])

   useEffect(() => {
      setValue('province_board', profileData.province_board)
      setValue('province_enployment', profileData.province_enployment)
   }, [provincesQuery.data?.length])

   return (
      <form onSubmit={handleSubmit(onSubmit)}>
         <div className='flex flex-col gap-8 my-4 container mx-auto'>
            <div className='grid md:grid-cols-3 grid-cols-1 gap-4'>
               <RegisterSelect
                  required
                  name='title'
                  register={register}
                  errorMessage={errors.title?.message}
                  label='Titolo'
                  options={titleOptions}
                  placeholder='Seleziona titolo'
               />
               <RegisterInput
                  required
                  errorMessage={errors.first_name?.message}
                  name='first_name'
                  infoText='Inserire il nome completo, come da codice fiscale'
                  register={register}
                  label='Name'
                  placeholder='Mario'
               />
               <RegisterInput
                  required
                  errorMessage={errors.last_name?.message}
                  name='last_name'
                  infoText='Inserire il cognome completo, come da codice fiscale'
                  register={register}
                  label='Cognome'
                  placeholder='Rossi'
               />
            </div>
            <FormControl>
               <div className='flex justify-between'>
                  <FormLabel
                     className='text-dark-gray flex justify-between text-sm'
                     fontSize={'14px'}>
                     Email
                  </FormLabel>
               </div>

               <Input
                  disabled
                  defaultValue={userData?.user_data.email}
                  border={'2px solid'}
                  height={'48px'}
                  shadow='md'
               />
            </FormControl>
            <div className='grid md:grid-cols-[70%_27%] grid-cols-1 justify-between gap-4'>
               <RegisterSelect
                  required
                  name='profession'
                  register={register}
                  options={professionQuery.data?.data.data || []}
                  errorMessage={errors.profession?.message}
                  label='Professione'
                  placeholder='Seleziona professione '
               />
               <RegisterSelect
                  required
                  name='territory'
                  register={register}
                  options={ospedaleOptions}
                  errorMessage={errors.territory?.message}
                  label='Territorio'
                  placeholder='Seleziona'
               />
            </div>
            <div className='grid md:grid-cols-2 grid-cols-1 gap-4'>
               {specializations.length > 0 && (
                  <RegisterSelect
                     required
                     name='specialization'
                     autoSelectFirstOption={false}
                     register={register}
                     errorMessage={errors.specialization?.message}
                     label='Disciplina'
                     options={specializations}
                     placeholder='Seleziona Disciplina '
                  />
               )}
               {subSpecializations.length > 0 && (
                  <RegisterSelect
                     required
                     options={subSpecializations}
                     name='sub_specialization'
                     register={register}
                     errorMessage={errors.sub_specialization?.message}
                     label='Specializzazione'
                     placeholder='Seleziona Specializzazione '
                  />
               )}
               {specializations[1] && (
                  <RegisterSelect
                     infoText='Se ha ulteriori titoli, la invitiamo ad indicare qui la 2 disciplina'
                     required={false}
                     autoSelectFirstOption={false}
                     name={'specialization_2' as keyof TRegisterSecondStepForm}
                     register={register}
                     label={'Disciplina 2'}
                     options={otherSpecializations}
                     placeholder={'Seleziona Disciplina 2'}
                  />
               )}
               {specializations[2] && (
                  <RegisterSelect
                     infoText='Se ha ulteriori titoli, la invitiamo ad indicare qui la 3 disciplina'
                     required={false}
                     autoSelectFirstOption={false}
                     name={'specialization_3' as keyof TRegisterSecondStepForm}
                     register={register}
                     label={'Disciplina 3'}
                     options={otherSpecializations.filter(
                        (spec: any) => spec.name !== watchSpecialization2
                     )}
                     placeholder={'Seleziona Disciplina 3'}
                  />
               )}
            </div>
            <div className='grid md:grid-cols-3 items-end grid-cols-1 gap-4'>
               <RegisterSelect
                  required
                  name='employment'
                  register={register}
                  errorMessage={errors.employment?.message}
                  label='Operatività'
                  options={operativitàOptions}
                  placeholder='Seleziona Operatività'
               />
               <RegisterSelect
                  required
                  name='province_enployment'
                  register={register}
                  selectId
                  options={provincesQuery.data || []}
                  errorMessage={errors.province_enployment?.message}
                  label='Provincia attività lavorativa'
                  placeholder='Seleziona provincia'
               />
               <RegisterInput
                  required
                  errorMessage={errors.regione_enployment?.message}
                  name='regione_enployment'
                  infoText='Se ha selezionato "privo di occupazione" indicare l&apos;ultima provincia nella quale si è lavorato'
                  register={register}
                  disabled
                  label='Regione attività lavorativa'
                  placeholder='Lombardia'
               />
               <RegisterSelect
                  required
                  name='board_member'
                  register={register}
                  options={ordineOptions}
                  errorMessage={errors.board_member?.message}
                  label='Ordine professionale'
                  placeholder='Seleziona Ordine'
               />
               <RegisterSelect
                  required
                  name='province_board'
                  register={register}
                  selectId
                  errorMessage={errors.province_board?.message}
                  label="Provincia di iscrizione all'ordine"
                  options={provincesQuery.data || []}
                  placeholder=''
               />
               <RegisterInput
                  required
                  errorMessage={errors.board_number?.message}
                  name='board_number'
                  infoText="Indicare il numero d'iscrizione all'ordine professionale"
                  register={register}
                  label='Numero iscrizione Ordine'
                  placeholder='Inserisci numero'
               />
            </div>
            <div className='w-full justify-end flex'>
               <Button type='submit' variant={'primary'} w='150px' height={'55px'}>
                  Avanti
               </Button>
            </div>
         </div>
      </form>
   )
}
